// src/rtkQuery/features/services/selfSchedule/selfScheduleApi.slice.ts
import { baseApi } from '@/rtkQuery/features/services/baseApi';
import { SELF_SCHEDULE_ENDPOINTS } from '@/rtkQuery/config/selfScheduleConfig';
import { buildQueryWithParams } from '@/rtkQuery/lib/helper';
import type {
    GetProviderAvailabilityRequest,
    GetProviderAvailabilityResponse,
    ScheduleAppointmentRequest,
    ScheduleAppointmentResponse,
    AppointmentResponse,
} from '@/rtkQuery/features/models/selfSchedule.model';

export const selfScheduleApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getProviderAvailability: builder.query<
            GetProviderAvailabilityResponse,
            GetProviderAvailabilityRequest
        >({
            query: buildQueryWithParams<GetProviderAvailabilityRequest>(
                SELF_SCHEDULE_ENDPOINTS.GET_PROVIDER_AVAILABILITY,
                'GET'
            ),
            transformResponse: (response: GetProviderAvailabilityResponse, meta) => {
                (meta as any).status = 'success';
                (meta as any).error = null;
                return response;
            },
            transformErrorResponse: (response: any, meta) => {
                const errorMessage = response.data?.message || 'Failed to fetch provider availability';
                (meta as any).status = 'error';
                (meta as any).error = errorMessage;

                return {
                    status: response.status,
                    message: errorMessage,
                    errors: response.data?.errors || {},
                };
            },
        }),

        scheduleAppointment: builder.mutation<
            ScheduleAppointmentResponse,
            ScheduleAppointmentRequest
        >({
            query: (params: ScheduleAppointmentRequest) => { 
                const queryParams = new URLSearchParams({
                    startTime: params.startTime,
                    providerid: params.providerid.toString(),
                });
                return {
                    url: `${SELF_SCHEDULE_ENDPOINTS.POST_SCHEDULE_APPOINTMENT}?${queryParams.toString()}`,
                    method: 'POST',
                };
            },
            transformResponse: (response: ScheduleAppointmentResponse, meta) => {
                (meta as any).status = 'success';
                (meta as any).error = null;
                return response;
            },
            transformErrorResponse: (response: any, meta) => {
                const errorMessage = response.data?.message || 'Failed to schedule appointment';
                (meta as any).status = 'error';
                (meta as any).error = errorMessage;

                return {
                    status: response.status,
                    message: errorMessage,
                    errors: response.data?.errors || {},
                };
            },
        }),

        getRecentAppointments: builder.query<AppointmentResponse, void>({
            query: () => ({
                url: SELF_SCHEDULE_ENDPOINTS.GET_RECENT_APPOINTMENTS,
                method: 'GET',
            }),
            transformResponse: (response: AppointmentResponse, meta) => {
                (meta as any).status = 'success';
                (meta as any).error = null;
                return response;
            },
            transformErrorResponse: (response: any, meta) => {
                const errorMessage = response.data?.message || 'Failed to fetch recent appointments';
                (meta as any).status = 'error';
                (meta as any).error = errorMessage;

                return {
                    status: response.status,
                    message: errorMessage,
                    errors: response.data?.errors || {},
                };
            },
        }),
    }),

    overrideExisting: false,
});

export const {
    useGetProviderAvailabilityQuery,
    useLazyGetProviderAvailabilityQuery,
    useScheduleAppointmentMutation,
    useGetRecentAppointmentsQuery,
    useLazyGetRecentAppointmentsQuery,
} = selfScheduleApi;
